import {agentViewQueueDisplayGroupValidator, type AgentViewQueueDisplayGroup} from "../workspaceCapabilities/agentViewWorkspaceCapability/domain/AgentView";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import type {ComplexFilter, Filter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {z as zod} from "zod";

export type CsvDownloadOrderBy = {column: string; direction: "asc" | "desc"};
export type CsvDownloadRequest = {
    workspaceId: Uuid;
    userId: Uuid;
    columns: Array<string>;
    filters?: ComplexFilter;
    orderBy?: Array<CsvDownloadOrderBy>;
};
export type EventContainerCsvDownloadRequest = CsvDownloadRequest & {templateId: Uuid};
export type EventCsvDownloadRequest = CsvDownloadRequest & {templateId: Uuid};
/** Optional exact-set selection for Users exports. IDs are workspace-user row IDs
 * in the caller's intended output order, not platform user identities. */
export type UsersCsvDownloadRequest = CsvDownloadRequest & {selectedIds?: Array<Uuid>};
export type InvitationsCsvDownloadRequest = CsvDownloadRequest;
export type TransferCsvDownloadRequest = CsvDownloadRequest;
/** Day Plan row exports use the caller's workspace-agent scope; the current
 * plan date is resolved by the owning Agent View capability. */
export type AgentViewDayPlanCsvDownloadRequest = CsvDownloadRequest & {
    agentUserId?: Uuid;
    eventTemplateId?: Uuid;
    leadCategory?: AgentViewQueueDisplayGroup;
    rowId?: Uuid;
};
/** Dulux page field aliases are normalized to owned Domain Aggregate field IDs
 * before they cross the boundary. */
export type DuluxDayPlanCsvDownloadRequest = CsvDownloadRequest & {templateId: Uuid; rowId?: Uuid};
/** Legacy failed-transfer CSV columns, now emitted by the movement owner. */
export type FailedTransferItemCsvColumn =
    | "movementItemId" | "operationId" | "eventContainerId" | "expectedRevisionId"
    | "status" | "failureCode" | "failureDetails";
export type FailedTransferItemsCsvDownloadRequest = Omit<CsvDownloadRequest, "columns"> & {
    columns: Array<FailedTransferItemCsvColumn>;
    operationId: Uuid;
};
export type CsvTrackerClientFilter = {
    columnId: string;
    operation: string;
    value?: unknown;
    values?: Array<string>;
};
export type AmaronTrackerCsvDownloadRequest = CsvDownloadRequest & {
    templateId: Uuid;
    viewLevel: 1 | 2 | 3 | 4;
    notificationFilter?: string;
    kanbanColumnSlug?: string | null;
    dispositionFilterDispositionIds?: Array<Uuid>;
    clientFilterLineItems?: Array<CsvTrackerClientFilter>;
};
export type InvitationCsvColumn =
    | "allowedEmail" | "allowedPhoneNumber" | "status" | "userTemplateId"
    | "organisationalUnits" | "parentUserId" | "createdAt" | "createdBy"
    | "expiresAt" | "submittedAt" | "submittedBy" | "updatedAt" | "updatedBy";
export type TransferCsvColumn =
    | "operationId" | "actorUserId" | "actorRole" | "status"
    | "completedAt" | "operationType" | "operationMode" | "reason" | "createdAt";
export type CsvDownloadError = {success: false; error: string};

export const CSV_DOWNLOAD_LIMITS = {
    columns: 256,
    sortKeys: 16,
    filterLeaves: 256,
    filterDepth: 10,
    encodedBodyBytes: 32 * 1024,
    selectedUserIds: 128,
} as const;

export const INVITATION_CSV_COLUMNS: Array<InvitationCsvColumn> = [
    "allowedEmail", "allowedPhoneNumber", "status", "userTemplateId", "organisationalUnits",
    "parentUserId", "createdAt", "createdBy", "expiresAt", "submittedAt", "submittedBy", "updatedAt", "updatedBy",
];
export const TRANSFER_CSV_COLUMNS: Array<TransferCsvColumn> = [
    "operationId", "actorUserId", "actorRole", "status", "completedAt", "operationType", "operationMode", "reason", "createdAt",
];
export const FAILED_TRANSFER_ITEM_CSV_COLUMNS: Array<FailedTransferItemCsvColumn> = [
    "movementItemId", "operationId", "eventContainerId", "expectedRevisionId", "status", "failureCode", "failureDetails",
];

/** Root filter group has depth 1; each nested group adds one. Tracker client
 * filters share the 256-leaf budget with SQL filters; disposition IDs/values
 * each have their own 256-item ceiling. Traversal precedes recursive parsing. */
export function csvDownloadExceedsStructuralLimits(input: unknown): boolean {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
        return false;
    }
    const body = input as Record<string, unknown>;
    for (const [key, limit] of [
        ["columns", CSV_DOWNLOAD_LIMITS.columns],
        ["orderBy", CSV_DOWNLOAD_LIMITS.sortKeys],
        ["dispositionFilterDispositionIds", CSV_DOWNLOAD_LIMITS.filterLeaves],
        ["selectedIds", CSV_DOWNLOAD_LIMITS.selectedUserIds],
    ] as const) {
        if (Array.isArray(body[key]) && body[key].length > limit) {
            return true;
        }
    }
    const clients = Array.isArray(body.clientFilterLineItems) ? body.clientFilterLineItems : [];
    let leaves = clients.length;
    if (leaves > CSV_DOWNLOAD_LIMITS.filterLeaves) {
        return true;
    }
    for (const client of clients) {
        if (typeof client === "object" && client !== null && "values" in client &&
            Array.isArray(client.values) && client.values.length > CSV_DOWNLOAD_LIMITS.filterLeaves) {
            return true;
        }
    }
    const stack: Array<{node: unknown; depth: number; exiting?: boolean}> = [{node: body.filters, depth: 1}];
    const seen = new Set<object>();
    while (stack.length > 0) {
        const entry = stack.pop();
        if (entry === undefined || typeof entry.node !== "object" || entry.node === null) {
            continue;
        }
        if (entry.exiting === true) {
            seen.delete(entry.node);
            continue;
        }
        if ("filters" in entry.node && Array.isArray(entry.node.filters)) {
            if (seen.has(entry.node)) {
                return true;
            }
            seen.add(entry.node);
            stack.push({node: entry.node, depth: entry.depth, exiting: true});
            if (entry.depth > CSV_DOWNLOAD_LIMITS.filterDepth) {
                return true;
            }
            for (const node of entry.node.filters) {
                stack.push({node: node, depth: entry.depth + 1});
            }
        } else {
            leaves += 1;
            if (leaves > CSV_DOWNLOAD_LIMITS.filterLeaves) {
                return true;
            }
        }
    }
    return false;
}

const identifier = zod.string().min(1).refine((value) => value.trim().length > 0);
const filterLeafValidator: zod.ZodType<Filter> = zod.strictObject({
    column: identifier,
    operator: identifier,
    value: zod.unknown(),
}).refine((leaf) => Object.hasOwn(leaf, "value") && leaf.value !== undefined, {
    message: "Filter value must be present (explicit null is allowed).",
});
const filterValidator: zod.ZodType<ComplexFilter> = zod.lazy(() => zod.strictObject({
    logic: zod.enum(["AND", "OR"]),
    filters: zod.array(zod.union([filterLeafValidator, filterValidator])),
}));
const commonShape = {
    workspaceId: uuidZodValidator,
    userId: uuidZodValidator,
    columns: zod.array(identifier).min(1).max(CSV_DOWNLOAD_LIMITS.columns),
    filters: filterValidator.optional(),
    orderBy: zod.array(zod.strictObject({
        column: identifier,
        direction: zod.enum(["asc", "desc"]),
    })).max(CSV_DOWNLOAD_LIMITS.sortKeys).optional(),
};
const boundedInput = zod.unknown().superRefine((input, context) => {
    if (csvDownloadExceedsStructuralLimits(input)) {
        context.addIssue({code: "custom", message: "CSV request exceeds structural limits."});
    }
});

export const eventContainerCsvDownloadRequestValidator: zod.ZodType<EventContainerCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, templateId: uuidZodValidator}));
export const eventCsvDownloadRequestValidator: zod.ZodType<EventCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, templateId: uuidZodValidator}));
const selectedUserIdsValidator = zod.array(uuidZodValidator).min(1).max(CSV_DOWNLOAD_LIMITS.selectedUserIds)
    .refine((ids) => new Set(ids).size === ids.length, {message: "Selected Users must not contain duplicate IDs."});
export const usersCsvDownloadRequestValidator: zod.ZodType<UsersCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, selectedIds: selectedUserIdsValidator.optional()})).superRefine((request, context) => {
        if (request.selectedIds !== undefined && (request.filters !== undefined || request.orderBy !== undefined)) {
            context.addIssue({code: "custom", message: "Selected Users exports cannot include filters or sorting."});
        }
    });
export const invitationsCsvDownloadRequestValidator: zod.ZodType<InvitationsCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, columns: zod.array(zod.enum(INVITATION_CSV_COLUMNS)).min(1).max(256)}));
export const transferCsvDownloadRequestValidator: zod.ZodType<TransferCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, columns: zod.array(zod.enum(TRANSFER_CSV_COLUMNS)).min(1).max(256)}));
export const agentViewDayPlanCsvDownloadRequestValidator: zod.ZodType<AgentViewDayPlanCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, agentUserId: uuidZodValidator.optional(), eventTemplateId: uuidZodValidator.optional(),
        leadCategory: agentViewQueueDisplayGroupValidator.optional(), rowId: uuidZodValidator.optional()}));
export const duluxDayPlanCsvDownloadRequestValidator: zod.ZodType<DuluxDayPlanCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, templateId: uuidZodValidator, rowId: uuidZodValidator.optional()}));
export const failedTransferItemsCsvDownloadRequestValidator: zod.ZodType<FailedTransferItemsCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({...commonShape, columns: zod.array(zod.enum(FAILED_TRANSFER_ITEM_CSV_COLUMNS)).min(1).max(256), operationId: uuidZodValidator}));
export const amaronTrackerCsvDownloadRequestValidator: zod.ZodType<AmaronTrackerCsvDownloadRequest> =
    boundedInput.pipe(zod.strictObject({
        ...commonShape,
        templateId: uuidZodValidator,
        viewLevel: zod.union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4)]),
        notificationFilter: zod.string().optional(),
        kanbanColumnSlug: zod.string().nullable().optional(),
        dispositionFilterDispositionIds: zod.array(uuidZodValidator).max(256).optional(),
        clientFilterLineItems: zod.array(zod.strictObject({
            columnId: identifier,
            operation: identifier,
            value: zod.unknown().optional(),
            values: zod.array(zod.string()).max(256).optional(),
        })).max(256).optional(),
    }));
